import axios from 'axios'
import { v4 } from 'uuid'

import {
  abortMultipartUpload,
  callPresignedUrl,
  completeMultipartUpload,
  createPresignedUrl,
  initiateMultipartUpload,
  validateUploadId,
} from 'api/storage'
import constants from '../../../../constants'
import i18n from '../../../../i18n'
import { getMediaDimensions } from 'utils/helpers'

const PART_SIZE = 20 * 1000 * 1000 //20MB

export const fileStatus = Object.freeze({
  new: 'NEW',
  uploading: 'UPLOADING',
  success: 'SUCCESS',
  uploadFailed: 'UPLOAD_FAILED',
  invalid: 'INVALID',
})

export const initialState = {
  media: [],
  isLoading: false,
  success: null,
  error: null,
  valid: null,
  fileToReplace: null,
}

export const actions = Object.freeze({
  addFile: 'ADD_FILE',
  removeFile: 'REMOVE_FILE',
  startReplace: 'START_REPLACE',
  resetReplace: 'RESET_REPLACE',
  replaceFile: 'REPLACE_FILE',
  editFileInfo: 'EDIT_FILE_INFO',
  updatePlacementGroupId: 'UPDATE_PLACEMENT_GROUP_ID',

  fileUploadInitiate: 'FILE_UPLOAD_INITIATE',
  fileUploadFail: 'FILE_UPLOAD_FAIL',
  fileUploadSuccess: 'FILE_UPLOAD_SUCCESS',
  uploadComplete: 'UPLOAD_COMPLETE',

  addFilesAsync: 'ADD_FILE_ASYNC',
  uploadFilesAsync: 'UPLOAD_FILES_ASYNC',
  uploadCompleteAsync: 'UPLOAD_COMPLETE_ASYNC',
  removeFileAsync: 'REMOVE_FILE_ASYNC',
  replaceFileAsync: 'REPLACE_FILE_ASYNC',
  retryUploadAsync: 'RETRY_UPLOAD_ASYNC',
  addFileToGroupAsync: 'ADD_FILE_TO_GROUP_ASYNC',
  replaceFileInGroupAsync: 'REPLACE_FILE_IN_GROUP_ASYNC',
  removeGroupAsync: 'REMOVE_GROUP_ASYNC',
  removeFromGroupAsync: 'REMOVE_FROM_GROUP_ASYNC',
  removeFiles: 'REMOVE_FILES',
})

export function mediaUploadReducer(state, action) {
  switch (action.type) {
    case actions.addFile: {
      const existingMedia = state.media.filter((x) => x.status !== fileStatus.invalid)

      const existingGroupIndexes = existingMedia
        .map((file) => file.group_index)
        .filter((value) => value !== null && value !== undefined)

      let maxGroupIndex = existingGroupIndexes.length ? Math.max(...existingGroupIndexes) : -1

      // - if group_index already exists (for files in a group) — don't change it
      // - if this is a new "row" (placement_group_id === null and group_index == null) — give a new group_index at the end
      const preparedNewFiles = action.payload.map((file) => {
        if (file.group_index !== null && file.group_index !== undefined) {
          return file
        }

        if (file.placement_group_id) {
          return file
        }

        maxGroupIndex += 1
        return {
          ...file,
          group_index: maxGroupIndex,
        }
      })

      const mergedMedia = [...existingMedia, ...preparedNewFiles]

      const newMedia = mergedMedia.sort((a, b) => {
        const aIdx = a.group_index ?? 0
        const bIdx = b.group_index ?? 0
        return aIdx - bIdx
      })

      return {
        ...state,
        media: newMedia,
        valid: newMedia.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed),
      }
    }
    case actions.removeFile: {
      const fileToRemove = action.payload
      URL.revokeObjectURL(fileToRemove?.preview)

      const media = state.media
        .filter((x) => x.id !== fileToRemove.id)
        .sort((a, b) => {
          const aIdx = a.group_index ?? 0
          const bIdx = b.group_index ?? 0
          return aIdx - bIdx
        })

      return {
        ...state,
        media,
        valid: media.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed),
      }
    }
    case actions.removeFiles: {
      const filesToRemove = Array.isArray(action.payload) ? action.payload : [action.payload]
      
      // Revoke object URLs for all files
      filesToRemove.forEach((file) => {
        URL.revokeObjectURL(file?.preview)
      })

      // Remove all files from media array at once
      const fileIdsToRemove = new Set(filesToRemove.map((f) => f.id))
      const media = state.media
        .filter((x) => !fileIdsToRemove.has(x.id))
        .sort((a, b) => {
          const aIdx = a.group_index ?? 0
          const bIdx = b.group_index ?? 0
          return aIdx - bIdx
        })

      return {
        ...state,
        media,
        valid: media.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed),
      }
    }
    case actions.startReplace: {
      const fileToReplace = action.payload
      if (!fileToReplace) throw new Error('Invalid file to replace')

      return {
        ...state,
        fileToReplace,
      }
    }
    case actions.resetReplace: {
      const { fileToReplace } = state
      if (!fileToReplace) {
        return {
          ...state,
          error: action.payload?.error,
        }
      }

      // Restore original file by id
      const media = state.media.map((file) =>
        file.id === fileToReplace.id ? fileToReplace : file,
      )

      return {
        ...state,
        media,
        fileToReplace: null,
        error: action.payload?.error, // optional
        valid:
          media.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed) &&
          !action.payload?.error,
      }
    }
    case actions.replaceFile: {
      const newFile = action.payload

      const { fileToReplace } = state
      if (!fileToReplace) throw new Error('Invalid file ID')

      // Replace file by id, keep group_index as is (already set on newFile)
      const media = state.media.map((x) => (x.id === fileToReplace.id ? newFile : x))

      return {
        ...state,
        media,
        valid: media.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed),
      }
    }
    case actions.editFileInfo: {
      const media = state.media
      const { id: fileId } = action.payload
      const file = media.find((x) => x.id === fileId)

      if (!file) throw new Error('Invalid file ID')

      const newMedia = constructEditObject(media, action.payload)
      const valid = newMedia.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed)

      return {
        ...state,
        media: newMedia,
        valid,
      }
    }
    case actions.updatePlacementGroupId: {
      const { fileId, placement_group_id } = action.payload
      const newMedia = state.media.map((file) => {
        if (file.id === fileId) {
          return { ...file, placement_group_id }
        }
        return file
      })

      return {
        ...state,
        media: newMedia,
      }
    }
    case actions.fileUploadInitiate: {
      const newFiles = state.media.map((file) => {
        if (file.id === action.payload.id) {
          return { ...file, ...action.payload, status: fileStatus.uploading, error: '' }
        }
        return file
      })

      return {
        ...state,
        media: newFiles,
        isLoading: true,
      }
    }
    case actions.fileUploadFail: {
      const newFiles = state.media.map((file) => {
        if (file.id === action.payload.id) {
          let newFile = file

          return { ...newFile, status: fileStatus.uploadFailed, error: i18n.t('Uploads.errors.fileUploadFailed') }
        }
        return file
      })
      return {
        ...state,
        media: newFiles,
        isLoading: false,
        success: false,
        error: i18n.t('Uploads.errors.uploadFailed'),
      }
    }
    case actions.fileUploadSuccess: {
      const uploadedFileId = action.payload.id

      const newFiles = state.media.map((file) => {
        if (file.id === uploadedFileId) {
          return { ...file, status: fileStatus.success, error: '' }
        }
        return file
      })

      let nextState = {
        ...state,
        media: newFiles,
      }

      // if we have a file to replace, we need to clear it after the upload is successful
      if (state.fileToReplace) {
        const oldFileId = state.fileToReplace.id

        const oldFileStillExists = newFiles.some((file) => file.id === oldFileId)

        if (!oldFileStillExists) {
          URL.revokeObjectURL(state.fileToReplace?.preview)
          nextState = {
            ...nextState,
            fileToReplace: null,
          }
        }
      }

      return nextState
    }
    default:
      throw new Error(`Unknown action type: ${action.type}`)
  }
}

export const asyncActionHandlers = {
  [actions.addFilesAsync]:
    ({ dispatch }) =>
      async (action) => {
        const media = [action.payload].flat()

        const validationResult = await Promise.all(media.map((file) => validateFileAsync(file)))

        const newFiles = await Promise.all(
          validationResult.map((result) => {
            const [file, error] = result
            return constructFileObject(file, error)
          }),
        )

        dispatch({ type: actions.addFile, payload: newFiles })

        if (newFiles.every((x) => x.status === fileStatus.invalid)) return

        const filesToUpload = newFiles.filter((x) => x.status === fileStatus.new)

        await Promise.all(filesToUpload.map((file) => uploadFile(dispatch, file)))
      },
  [actions.removeFileAsync]:
    ({ dispatch }) =>
      async (action) => {
        const file = action.payload
        abortUpload(file)
        dispatch({ type: actions.removeFile, payload: file })
      },
  [actions.replaceFileAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const state = getState()
        const fileToReplace = state.fileToReplace
        if (!fileToReplace) throw new Error('Missing or invalid fileToReplace')

        const newFile = [action.payload].flat()[0]
        const [file, error] = await validateFileAsync(newFile)

        if (error) {
          dispatch({ type: actions.resetReplace, payload: { error } })
          return
        }

        const fileToUpload = await constructFileObject(file, error)

        dispatch({ type: actions.replaceFile, payload: fileToUpload })

        try {
          await uploadFile(dispatch, fileToUpload)
        } catch (error) {
          dispatch({
            type: actions.resetReplace,
            payload: { error: error?.message || 'File upload failed' },
          })
          return
        }
      },
  [actions.retryUploadAsync]:
    ({ dispatch }) =>
      async (action) => {
        const file = action.payload
        await uploadFile(dispatch, file)
      },
  [actions.addFileToGroupAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { file, targetFileId, requiredPlacementType } = action.payload
        const state = getState()

        // Find target file
        const targetFile = state.media.find((x) => x.id === targetFileId)
        if (!targetFile) throw new Error('Target file not found')

        // Validate file
        const [validatedFile, error] = await validateFileAsync(file)
        if (error) {
          throw new Error(error)
        }

        // Create file object (this will calculate placement_type)
        const fileToUpload = await constructFileObject(validatedFile, error)

        // New file in group should share the same group_index as the target file (same "row" in list)
        fileToUpload.group_index = targetFile.group_index

        // Validate file placement_type matches required placement type
        if (fileToUpload.placement_type !== requiredPlacementType) {
          throw new Error(`File aspect ratio doesn't match required placement type. Expected: ${requiredPlacementType}, got: ${fileToUpload.placement_type}`)
        }

        // Generate placement_group_id
        const placement_group_id = v4()

        // Update both files with placement_group_id
        dispatch({ type: actions.updatePlacementGroupId, payload: { fileId: targetFileId, placement_group_id } })
        fileToUpload.placement_group_id = placement_group_id

        // New variation in group should belong to placement_media array
        fileToUpload.source = 'placement_media'

        // Add new file
        dispatch({ type: actions.addFile, payload: [fileToUpload] })

        // Upload the new file
        await uploadFile(dispatch, fileToUpload)
      },
  [actions.replaceFileInGroupAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { file, fileToReplaceId, requiredPlacementType } = action.payload
        const state = getState()

        // Find file to replace
        const fileToReplace = state.media.find((x) => x.id === fileToReplaceId)
        if (!fileToReplace) throw new Error('File to replace not found')

        // Store old file for potential restoration
        dispatch({ type: actions.startReplace, payload: fileToReplace })

        // Get placement_group_id from file to replace (to maintain group)
        const placement_group_id = fileToReplace.placement_group_id

        // Validate file
        const [validatedFile, error] = await validateFileAsync(file)
        if (error) {
          // Restore old file on validation error
          dispatch({ type: actions.resetReplace, payload: { error } })
          return
        }

        // Create file object (this will calculate placement_type)
        const fileToUpload = await constructFileObject(validatedFile, error)

        // New file should keep the same group_index as the file being replaced
        fileToUpload.group_index = fileToReplace.group_index

        // Validate file placement_type matches required placement type
        if (fileToUpload.placement_type !== requiredPlacementType) {
          // Restore old file on placement type mismatch
          dispatch({ type: actions.resetReplace, payload: { error: `File aspect ratio doesn't match required placement type. Expected: ${requiredPlacementType}, got: ${fileToUpload.placement_type}` } })
          return
        }

        // Set placement_group_id to maintain group
        fileToUpload.placement_group_id = placement_group_id

        // Save source of media / placement_media when replacing a file.
        fileToUpload.source = fileToReplace.source || 'media'
        
        // Replace file in place (keep old preview alive until upload succeeds)
        dispatch({ type: actions.replaceFile, payload: fileToUpload })

        try {
          // Upload the new file
          await uploadFile(dispatch, fileToUpload)
        } catch (error) {
          // If upload throws, restore old file and surface error
          dispatch({
            type: actions.resetReplace,
            payload: { error: error?.message || 'File upload failed' },
          })
          return
        }

    
      },
  [actions.removeGroupAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { groupId } = action.payload
        const state = getState()

        // Find all files in the group
        const filesInGroup = state.media.filter((file) => file.placement_group_id === groupId)

        // Abort uploads for all files in the group
        filesInGroup.forEach((file) => {
          abortUpload(file)
        })

        // Remove all files in the group at once
        dispatch({ type: actions.removeFiles, payload: filesInGroup })
      },
  [actions.removeFromGroupAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { fileId } = action.payload
        const state = getState()

        const fileToRemove = state.media.find((x) => x.id === fileId)
        if (!fileToRemove) throw new Error('File not found')

        const groupId = fileToRemove.placement_group_id

        if (!groupId) {
          // File is not in a group — use regular remove
          return dispatch({ type: actions.removeFileAsync, payload: fileToRemove })
        }

        abortUpload(fileToRemove)

        const remainingInGroup = state.media.filter(
          (f) => f.placement_group_id === groupId && f.id !== fileId,
        )

        dispatch({ type: actions.removeFile, payload: fileToRemove })

        remainingInGroup.forEach((file) => {
          dispatch({
            type: actions.editFileInfo,
            payload: {
              id: file.id,
              placement_group_id: null,
              source: 'media',
            },
          })
        })
      },
}

const abortUpload = (file) => {
  if (file.status === fileStatus.uploading) {
    file.cancelTokenSource.cancel()
    abortMultipartUpload(
      {
        upload_id: file.uploadId,
        object_key: file.objectKey,
      },
      { upload_type: 'media' },
    )
  }
}

const uploadFile = async (dispatch, file) => {
  if (file.status === fileStatus.new) {
    await uploadNewFile(dispatch, file)
  } else if (file.status === fileStatus.uploadFailed) {
    const {
      content: { isValid },
    } = await validateUploadId(
      {
        upload_id: file.uploadId,
        object_key: file.objectKey,
        expected_total_parts: file.expectedTotalParts,
      },
      { cancelToken: file.cancelTokenSource.token, upload_type: 'media' },
    )
    if (!isValid) await uploadNewFile(dispatch, file)
    else {
      const presignedUrls = await getPresignedUrls(dispatch, file)
      await uploadParts(dispatch, file, presignedUrls)
    }
  }
}

const uploadNewFile = async (dispatch, file) => {
  const initialUploadData = await initiateUpload(dispatch, file)

  if (!initialUploadData) return

  const newFile = { ...file, ...initialUploadData }
  const presignedUrls = await getPresignedUrls(dispatch, newFile)

  await uploadParts(dispatch, newFile, presignedUrls)
}

const initiateUpload = async (dispatch, file) => {
  let { content: initialUploadData, error } = await initiateMultipartUpload(
    {
      object_name: file.name,
      content_type: file.type,
    },
    { cancelToken: file.cancelTokenSource.token, upload_type: 'media' },
  )

  if (error) {
    dispatch({ type: actions.fileUploadFail, payload: { id: file.id } })
    return
  }

  initialUploadData = {
    uploadId: initialUploadData.upload_id,
    objectKey: initialUploadData.object_key,
  }

  dispatch({ type: actions.fileUploadInitiate, payload: { ...initialUploadData, id: file.id } })

  return initialUploadData
}

const getPresignedUrls = async (dispatch, file) => {
  const { content: presignedUrls, error } = await createPresignedUrl(
    {
      upload_id: file.uploadId,
      object_key: file.objectKey,
      start_part_number: file.totalUploadedParts + 1,
      total_parts: file.expectedTotalParts,
      total_uploaded_parts: file.totalUploadedParts,
    },
    { cancelToken: file.cancelTokenSource.token, upload_type: 'media' },
  )

  if (error) {
    dispatch({ type: actions.fileUploadFail, payload: { id: file.id } })
    return
  }

  return presignedUrls
}

const uploadParts = async (dispatch, file, presignedUrls) => {
  let totalUploadedParts = file.totalUploadedParts
  let expectedTotalParts = file.expectedTotalParts
  let partsData = file.partsData

  if (!presignedUrls) return

  for (let index = 0; index < expectedTotalParts; index++) {
    const offset = index * PART_SIZE
    const part = file.file.slice(offset, offset + PART_SIZE)
    const presignedUrl = presignedUrls[index] || ''
    let lastPercentCompleted = 0
    const {
      content: partUploadResult,
      success,
      error,
    } = await callPresignedUrl(presignedUrl, part, { cancelToken: file.cancelTokenSource.token }, (progress) => {
      if (progress > lastPercentCompleted) {
        lastPercentCompleted = progress
        dispatch({ type: actions.editFileInfo, payload: { id: file.id, progress } })
      }
    })

    if (success) {
      partsData = [...partsData, { part_number: Number(index) + 1, etag: partUploadResult.etag }]
      totalUploadedParts = totalUploadedParts + 1

      dispatch({ type: actions.editFileInfo, payload: { id: file.id, partsData, totalUploadedParts } })
    }

    if (error) {
      dispatch({ type: actions.fileUploadFail, payload: { id: file.id } })
    }
  }

  if (totalUploadedParts !== expectedTotalParts) return

  const {
    success: uploadCompleted,
    error: uploadCompletionFailed,
    content,
  } = await completeMultipartUpload(
    {
      upload_id: file.uploadId,
      object_key: file.objectKey,
      parts: partsData,
    },
    { cancelToken: file.cancelTokenSource.token, upload_type: 'media' },
  )

  dispatch({ type: actions.editFileInfo, payload: { id: file.id, url: content.location } })

  if (uploadCompleted) {
    dispatch({ type: actions.fileUploadSuccess, payload: { id: file.id } })
  }
  if (uploadCompletionFailed) {
    dispatch({ type: actions.fileUploadFail, payload: { id: file.id } })
  }
}

const constructFileObject = async (file, error = null) => {
  let expectedTotalParts = Math.ceil(file.size / PART_SIZE, PART_SIZE)

  const dimensions = await getMediaDimensions(file)
  
  // Calculate placement_type based on aspect ratio
  // aspectRatio = height/width, if > 1.4 => Reel, otherwise Feed
  const aspectRatio = dimensions.height / dimensions.width
  const placement_type = aspectRatio > 1.4 ? 'reel' : 'feed'

  return {
    file,
    filename: file.path,
    path: file.path,
    name: file.name,
    type: file.type,
    size: file.size,
    height: dimensions.height,
    width: dimensions.width,
    placement_type,
    placement_group_id: null,
    group_index: null,
    // By default, we consider the file to belong to the main media array.
    // For variations in group, source will be overridden in addFileToGroupAsync / replaceFileInGroupAsync.
    source: 'media',
    id: v4(),
    error: error ? error : null,
    partsData: [],
    expectedTotalParts: expectedTotalParts,
    totalUploadedParts: 0,
    uploadId: null,
    objectKey: null,
    cancelTokenSource: axios.CancelToken.source(),
    progress: 0,
    preview: URL.createObjectURL(file),
    url: null,
    status: error ? fileStatus.invalid : fileStatus.new,
  }
}

const constructEditObject = (media, payload) => {
  return media.map((file) => {
    if (file.id === payload.id && payload.progress) {
      const progress = Math.round((1 / file.expectedTotalParts) * (file.totalUploadedParts * 100 + payload.progress))
      payload = { ...payload, progress }
    }
    if (file.id === payload.id) {
      // Preserve placement_group_id if not in payload
      const placement_group_id = payload.placement_group_id !== undefined 
        ? payload.placement_group_id 
        : file.placement_group_id
      return { ...file, ...payload, placement_group_id }
    }

    return file
  })
}

async function validateFileAsync(file) {
  const fileSizeMB = Math.round(file.size / 1000 / 1000)
  let error

  if (file.type.startsWith('image')) {
    if (fileSizeMB > constants.MAX_IMG_UPLOAD_SIZE) {
      error = i18n.t('common.errors.imgSize', { size: constants.MAX_IMG_UPLOAD_SIZE.toFixed(1) })
    }
  } else if (file.type.startsWith('video')) {
    const video = document.createElement('video')
    video.src = window.URL.createObjectURL(file)

    await new Promise((resolve) => {
      video.addEventListener('loadedmetadata', () => {
        window.URL.revokeObjectURL(video.src)
        resolve()
      })
    })

    if (fileSizeMB > constants.MAX_VIDEO_UPLOAD_SIZE) {
      error = i18n.t('common.errors.videoSize', { size: constants.MAX_VIDEO_UPLOAD_SIZE.toFixed(1) / 1000 })
    }
  } else {
    error = i18n.t('common.errors.fileFormat')
  }

  return [file, error]
}

export function allFilesUploaded(media) {
  return media.every((x) => x.status === fileStatus.success)
}
